import './block-editor.css';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { Markdown } from 'tiptap-markdown';
import { Button, Space } from 'antd';
import { Bold, Heading2, List, Table as TableIcon } from 'lucide-react';

/** BlockEditor Props */
interface Props {
  /** 初始 Markdown 文字（受控：切換板塊時由父層傳入） */
  value: string;
  /** 每次內容變動後回呼，傳出 Markdown 字串 */
  onChange: (markdown: string) => void;
}

/**
 * 單一報告板塊的 WYSIWYG 富文本編輯器。
 *
 * 以 Markdown 格式進出（tiptap-markdown 橋接）：
 * - 進：`value` 直接作為 markdown content 傳入 TipTap，由 Markdown 擴充解析為 ProseMirror 節點。
 * - 出：每次 `onUpdate` 呼叫 `editor.storage.markdown.getMarkdown()` 取回 Markdown 字串，透過 `onChange` 回呼。
 *
 * 工具列提供非技術稽核員所需的基本格式：粗體、標題、清單、表格。
 */
export function BlockEditor({ value, onChange }: Props) {
  const editor = useEditor({
    /** SSR 安全：明確設為 false 以避免 hydration 警告（Vite SPA 通常不需要，但保守設置） */
    immediatelyRender: false,
    extensions: [
      StarterKit,
      /** 表格支援（稽核報告常需資料表格，不啟用縮放以降低複雜度） */
      Table.configure({ resizable: false }),
      TableRow,
      TableHeader,
      TableCell,
      /** Markdown 橋接：讓 TipTap 接受 Markdown 作為 content，並可序列化回 Markdown */
      Markdown.configure({ html: false, transformPastedText: true }),
    ],
    /** tiptap-markdown 會將此 Markdown 字串解析為 ProseMirror 文件 */
    content: value,
    onUpdate: ({ editor: e }) => {
      /**
       * 透過 tiptap-markdown 的 storage 取回 Markdown 字串。
       * storage 型別未被 @tiptap/core 宣告，需做安全存取。
       */
      const storage = e.storage as { markdown?: { getMarkdown(): string } };
      if (!storage.markdown) {
        // 防御：tiptap-markdown 未注册時不可發空字串覆蓋原內容（否則保存會清空板塊）
        return;
      }
      onChange(storage.markdown.getMarkdown());
    },
  });

  if (!editor) return null;

  return (
    <div>
      {/* 簡易工具列：僅提供非技術稽核員日常使用的格式按鈕 */}
      <Space size={4} style={{ marginBottom: 8 }}>
        <Button
          size="small"
          title="粗體"
          icon={<Bold size={14} />}
          type={editor.isActive('bold') ? 'primary' : 'default'}
          onClick={() => editor.chain().focus().toggleBold().run()}
        />
        <Button
          size="small"
          title="標題"
          icon={<Heading2 size={14} />}
          type={editor.isActive('heading', { level: 2 }) ? 'primary' : 'default'}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        />
        <Button
          size="small"
          title="清單"
          icon={<List size={14} />}
          type={editor.isActive('bulletList') ? 'primary' : 'default'}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        />
        <Button
          size="small"
          title="插入表格"
          icon={<TableIcon size={14} />}
          onClick={() =>
            editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
          }
        />
      </Space>

      {/* TipTap 編輯區域（zhcb-block-editor 作用域套用 block-editor.css 的表格/標題/清單排版） */}
      <div
        className="zhcb-block-editor"
        style={{
          border: '1px solid #e2e8f0',
          borderRadius: 6,
          padding: '8px 12px',
          minHeight: 120,
          cursor: 'text',
        }}
        onClick={() => editor.commands.focus()}
      >
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
